import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowLeft } from "react-icons/fi";
import { useApi } from "../../../../Common/Hooks/useApi";
import { CreditTransaction } from "../../../../Common/Hooks/useApi/Schemas";
import { isErr } from "../../../../Common/Utils/Result";
import { Button } from "../../../../Components/Button";
import {
  Card,
  HistoryHeader,
  BackButton,
  InvoicesList,
  InvoiceItem,
  InvoiceDate,
  InvoiceDescription,
  TransactionAmount,
  LoadMoreRow,
  SkeletonBox,
} from "./Styles";

interface CreditTransactionsHistoryProps {
  onBack: () => void;
}

/** Every credit that came in or went out, newest first, ten at a time. */
export const CreditTransactionsHistory = ({ onBack }: CreditTransactionsHistoryProps) => {
  const { t, i18n } = useTranslation();
  const { getCreditTransactionsEndpoint } = useApi();
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);

  const loadPage = useCallback(
    async (cursor?: string) => {
      setIsLoading(true);
      setHasFailed(false);
      const result = await getCreditTransactionsEndpoint(cursor);
      if (isErr(result)) {
        setHasFailed(true);
      } else {
        setTransactions((loaded) => (cursor ? [...loaded, ...result.value.transactions] : result.value.transactions));
        setNextCursor(result.value.nextCursor);
      }
      setIsLoading(false);
    },
    [getCreditTransactionsEndpoint]
  );

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const formatDate = (date: Date) =>
    date.toLocaleDateString(i18n.language, { day: "numeric", month: "long", year: "numeric" });

  const describe = (transaction: CreditTransaction) =>
    transaction.type === "DD_CONSUMPTION" && transaction.dueDiligenceCase
      ? t("pricing.subscriptionContent.creditHistory.types.DD_CONSUMPTION_NAMED", {
          name: transaction.dueDiligenceCase.name || transaction.dueDiligenceCase.alias,
          // React escapes it already: i18next escaping too turns ' into &#39;
          interpolation: { escapeValue: false },
        })
      : t(`pricing.subscriptionContent.creditHistory.types.${transaction.type}`);

  const isFirstLoad = isLoading && transactions.length === 0;

  return (
    <>
      <HistoryHeader>
        <BackButton onClick={onBack}>
          <FiArrowLeft />
          {t("pricing.subscriptionContent.creditHistory.backButton")}
        </BackButton>
      </HistoryHeader>

      <Card>
        {isFirstLoad && (
          <InvoicesList>
            {[1, 2, 3].map((i) => (
              <InvoiceItem key={i}>
                <div>
                  <SkeletonBox width="12rem" height="1rem" style={{ marginBottom: "0.25rem" }} />
                  <SkeletonBox width="8rem" height="0.875rem" />
                </div>
                <SkeletonBox width="3rem" height="1rem" />
              </InvoiceItem>
            ))}
          </InvoicesList>
        )}

        {!isFirstLoad && transactions.length === 0 && !hasFailed && (
          <div style={{ padding: "2rem", textAlign: "center", color: "#6B7280", fontSize: "0.875rem" }}>
            {t("pricing.subscriptionContent.creditHistory.empty")}
          </div>
        )}

        {transactions.length > 0 && (
          <InvoicesList>
            {transactions.map((transaction) => (
              <InvoiceItem key={transaction.id}>
                <div>
                  <InvoiceDate>{describe(transaction)}</InvoiceDate>
                  <InvoiceDescription>
                    {formatDate(transaction.createdAt)}
                    {transaction.expiresAt &&
                      ` · ${t("pricing.subscriptionContent.creditHistory.expiresOn", {
                        date: formatDate(transaction.expiresAt),
                      })}`}
                  </InvoiceDescription>
                </div>
                <TransactionAmount $positive={transaction.amount > 0}>
                  {transaction.amount > 0
                    ? `+${transaction.amount.toLocaleString(i18n.language)}`
                    : `−${Math.abs(transaction.amount).toLocaleString(i18n.language)}`}
                </TransactionAmount>
              </InvoiceItem>
            ))}
          </InvoicesList>
        )}

        {hasFailed && (
          <div style={{ padding: "1rem 0", textAlign: "center", color: "#6B7280", fontSize: "0.875rem" }}>
            {t("pricing.subscriptionContent.creditHistory.loadFailed")}
          </div>
        )}

        {(nextCursor || hasFailed) && !isFirstLoad && (
          <LoadMoreRow>
            <Button onClick={() => loadPage(nextCursor ?? undefined)} isLoading={isLoading}>
              {hasFailed
                ? t("pricing.subscriptionContent.creditHistory.retryButton")
                : t("pricing.subscriptionContent.creditHistory.loadMoreButton")}
            </Button>
          </LoadMoreRow>
        )}
      </Card>
    </>
  );
};
